import React from 'react';
import { Drawer, Typography } from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTitleHistory } from '../../core/modules/book/hooks/useTitleHistory';
import { Spinner } from '../../components/Spinner';
import NoteItem, { INote } from './NoteItem';

const { Title } = Typography;

interface INotesOverlayProps {
  visible: boolean;
  onClose: () => void;
  book: Record<string, any>;
}

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #8c8c8c;
  font-size: 14px;
`;

const RefreshingBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 24px;
  font-size: 12px;
  color: #8c8c8c;
  border-bottom: 1px solid #f5f5f5;
`;

const NotesOverlay: React.FC<INotesOverlayProps> = ({ visible, onClose, book }) => {
  const resolveSource = (): {
    source: string;
    identifier: { amazonasin?: string; fiction_id?: string; work_id?: string };
  } => {
    if (book['ASIN']) {
      return { source: 'bookstat', identifier: { amazonasin: book['ASIN'] } };
    }

    if (book['Fiction Id']) {
      return { source: 'royal-road', identifier: { fiction_id: String(book['Fiction Id']) } };
    }

    return { source: 'ao3', identifier: { work_id: String(book['work_id']) } };
  };

  const { source, identifier } = resolveSource();

  const { data: notes = [], isLoading, isError, isFetching } = useTitleHistory(
    source,
    identifier,
    visible
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner size='default' spinning indicator={<LoadingOutlined />} />
        </div>
      );
    }

    if (isError) {
      return <EmptyState>Failed to load notes. Please try again.</EmptyState>;
    }

    if (notes.length === 0) {
      return <EmptyState>No notes recorded for this title.</EmptyState>;
    }

    return notes.map((note: INote) => <NoteItem key={note.id} note={note} />);
  };

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      destroyOnClose
      placement='right'
      width={560}
      closable={false}
      bodyStyle={{ padding: 0 }}
    >
      {/* ── Header ── */}
      <DrawerHeader>
        <Title level={4} style={{ margin: 0 }}>Notes</Title>
        <CloseOutlined onClick={onClose} style={{ fontSize: 16, cursor: 'pointer', color: '#595959' }} />
      </DrawerHeader>

      {/* Small loader shown on top while cached notes refresh in the background */}
      {isFetching && !isLoading && (
        <RefreshingBar>
          <Spinner
            size='small'
            spinning
            indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />}
          />
          Updating…
        </RefreshingBar>
      )}

      {renderContent()}
    </Drawer>
  );
};

export default NotesOverlay;
